import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Trash2,
  X,
  Upload,
  Download,
  Send,
  Edit2,
  Save,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  FileSignature,
  Mail,
  Users,
  Search,
  RefreshCw,
} from 'lucide-react';
import DropZone from '../components/DropZone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SignatureDocuments({ embedded = false }) {
  const [documents, setDocuments] = useState([]);
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  const fileInputRef = useRef(null);
  
  // Form states
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    is_offer_letter: false,
    requires_signature: true,
  });
  
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    template_content: '',
    placeholders: [],
    is_offer_letter: false,
    requires_signature: true,
  });
  
  const [requestForm, setRequestForm] = useState({
    document_id: '',
    recipient_type: 'applicant',
    recipient_id: '',
    recipient_name: '',
    recipient_email: '',
    placeholder_values: {},
    send_email: true,
  });

  const [newPlaceholder, setNewPlaceholder] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, requestsRes, applicantsRes, personnelRes] = await Promise.all([
        axios.get(`${API}/admin/signature-documents`),
        axios.get(`${API}/admin/signature-requests`),
        axios.get(`${API}/ats/applicants`),
        axios.get(`${API}/personnel?status=Active`),
      ]);
      setDocuments(docsRes.data);
      setSignatureRequests(requestsRes.data);
      setApplicants(applicantsRes.data);
      // Sort personnel by last name
      const sorted = personnelRes.data.sort((a, b) => {
        const lastNameCompare = (a.last_name || '').localeCompare(b.last_name || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.first_name || '').localeCompare(b.first_name || '');
      });
      setPersonnel(sorted);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (fileOrEvent) => {
    const file = fileOrEvent?.target?.files
      ? fileOrEvent.target.files[0]
      : fileOrEvent;
    if (!file) return;
    
    if (!uploadForm.name) {
      toast.error('Please enter a document name first');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/admin/signature-documents/upload?name=${encodeURIComponent(uploadForm.name)}&description=${encodeURIComponent(uploadForm.description || '')}&is_offer_letter=${uploadForm.is_offer_letter}&requires_signature=${uploadForm.requires_signature}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document uploaded');
      setShowAddModal(false);
      setUploadForm({ name: '', description: '', is_offer_letter: false, requires_signature: true });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.name || !templateForm.template_content) {
      toast.error('Name and content are required');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/signature-documents`, {
        ...templateForm,
        document_type: 'template',
      });
      toast.success('Template created');
      setShowTemplateModal(false);
      setTemplateForm({
        name: '',
        description: '',
        template_content: '',
        placeholders: [],
        is_offer_letter: false,
        requires_signature: true,
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/signature-documents/${docId}`);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleSendRequest = async () => {
    if (!requestForm.document_id || !requestForm.recipient_email || !requestForm.recipient_name) {
      toast.error('Document, recipient name and email are required');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/signature-requests`, requestForm);
      toast.success('Signature request sent');
      setShowRequestModal(false);
      setRequestForm({
        document_id: '',
        recipient_type: 'applicant',
        recipient_id: '',
        recipient_name: '',
        recipient_email: '',
        placeholder_values: {},
        send_email: true,
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to send signature request');
    }
  };

  const handleResendRequest = async (requestId) => {
    try {
      await axios.post(`${API}/admin/signature-requests/${requestId}/resend`);
      toast.success('Email resent');
    } catch (error) {
      toast.error('Failed to resend email');
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Cancel this signature request?')) return;
    
    try {
      await axios.delete(`${API}/admin/signature-requests/${requestId}`);
      toast.success('Request cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const addPlaceholder = () => {
    if (newPlaceholder && !templateForm.placeholders.includes(newPlaceholder)) {
      setTemplateForm({
        ...templateForm,
        placeholders: [...templateForm.placeholders, newPlaceholder],
      });
      setNewPlaceholder('');
    }
  };

  const removePlaceholder = (placeholder) => {
    setTemplateForm({
      ...templateForm,
      placeholders: templateForm.placeholders.filter(p => p !== placeholder),
    });
  };

  const openRequestModal = (doc) => {
    setSelectedDocument(doc);
    setRequestForm({
      ...requestForm,
      document_id: doc.id,
      placeholder_values: doc.placeholders?.reduce((acc, p) => ({ ...acc, [p]: '' }), {}) || {},
    });
    setShowRequestModal(true);
  };

  const handleRecipientChange = (type, id) => {
    let name = '';
    let email = '';
    
    if (type === 'applicant' && id) {
      const applicant = applicants.find(a => a.id === id);
      if (applicant) {
        name = `${applicant.first_name} ${applicant.last_name}`;
        email = applicant.email || '';
      }
    } else if (type === 'personnel' && id) {
      const person = personnel.find(p => p.id === id);
      if (person) {
        name = `${person.first_name} ${person.last_name}`;
        email = person.email || '';
      }
    }
    
    setRequestForm({
      ...requestForm,
      recipient_type: type,
      recipient_id: id,
      recipient_name: name,
      recipient_email: email,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Signed': return 'bg-green-100 text-green-800';
      case 'Viewed': return 'bg-blue-100 text-blue-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const pendingCount = signatureRequests.filter(r => r.status === 'Pending' || r.status === 'Viewed').length;
  const signedCount = signatureRequests.filter(r => r.status === 'Signed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-foreground tracking-tight">
              Signature Documents
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage documents, templates, and signature requests
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTemplateModal(true)}>
              <Edit2 size={16} className="mr-2" />
              Create Template
            </Button>
            <Button onClick={() => setShowAddModal(true)}>
              <Upload size={16} className="mr-2" />
              Upload Document
            </Button>
          </div>
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">Manage documents, templates, and signature requests</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowTemplateModal(true)}>
              <Edit2 size={14} className="mr-1" />
              Create Template
            </Button>
            <Button size="sm" onClick={() => setShowAddModal(true)}>
              <Upload size={14} className="mr-1" />
              Upload Document
            </Button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <FileText className="text-primary" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{documents.length}</p>
              <p className="text-xs text-muted-foreground">Documents</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-100">
              <Clock className="text-yellow-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{pendingCount}</p>
              <p className="text-xs text-muted-foreground">Pending Signatures</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <CheckCircle className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{signedCount}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <FileSignature className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{documents.filter(d => d.is_offer_letter).length}</p>
              <p className="text-xs text-muted-foreground">Offer Letters</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="documents">
            <FileText size={16} className="mr-2" />
            Documents ({documents.length})
          </TabsTrigger>
          <TabsTrigger value="requests">
            <Send size={16} className="mr-2" />
            Signature Requests ({signatureRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="mt-4">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 text-sm font-medium">Document</th>
                    <th className="text-left p-3 text-sm font-medium">Type</th>
                    <th className="text-left p-3 text-sm font-medium">Tags</th>
                    <th className="text-left p-3 text-sm font-medium">Created</th>
                    <th className="text-left p-3 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="border-b hover:bg-muted/20">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <FileText size={18} className="text-primary" />
                          <div>
                            <p className="font-medium">{doc.name}</p>
                            {doc.description && (
                              <p className="text-xs text-muted-foreground">{doc.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline">
                          {doc.document_type === 'template' ? 'Template' : 'Upload'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {doc.is_offer_letter && (
                            <Badge className="bg-purple-100 text-purple-800">Offer Letter</Badge>
                          )}
                          {doc.requires_signature && (
                            <Badge className="bg-blue-100 text-blue-800">Signature</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {formatDate(doc.created_at)}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openRequestModal(doc)}
                            title="Request Signature"
                          >
                            <Send size={14} />
                          </Button>
                          {doc.document_type === 'upload' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(`${API}/admin/signature-documents/${doc.id}/download?token=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank')}
                              title="Download"
                            >
                              <Download size={14} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => handleDeleteDocument(doc.id)}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {documents.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground">
                        No documents yet. Upload a document or create a template to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="mt-4">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 text-sm font-medium">Document</th>
                    <th className="text-left p-3 text-sm font-medium">Recipient</th>
                    <th className="text-left p-3 text-sm font-medium">Status</th>
                    <th className="text-left p-3 text-sm font-medium">Sent</th>
                    <th className="text-left p-3 text-sm font-medium">Signed</th>
                    <th className="text-left p-3 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signatureRequests.map((req) => (
                    <tr key={req.id} className="border-b hover:bg-muted/20">
                      <td className="p-3">
                        <p className="font-medium">{req.document_name}</p>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{req.recipient_name}</p>
                          <p className="text-xs text-muted-foreground">{req.recipient_email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={getStatusColor(req.status)}>
                          {req.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {formatDate(req.sent_at)}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {req.signed_at ? formatDate(req.signed_at) : '-'}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {req.status !== 'Signed' && req.status !== 'Cancelled' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResendRequest(req.id)}
                                title="Resend Email"
                              >
                                <RefreshCw size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => handleCancelRequest(req.id)}
                                title="Cancel"
                              >
                                <X size={14} />
                              </Button>
                            </>
                          )}
                          {req.status === 'Signed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {/* View signed document */}}
                              title="View Signed"
                            >
                              <Eye size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {signatureRequests.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        No signature requests yet. Select a document and request a signature.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Document Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Upload Document</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Document Name *</label>
                <Input
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                  placeholder="e.g., Employment Agreement"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={uploadForm.is_offer_letter}
                    onChange={(e) => setUploadForm({ ...uploadForm, is_offer_letter: e.target.checked })}
                    className="rounded"
                  />
                  Offer Letter
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={uploadForm.requires_signature}
                    onChange={(e) => setUploadForm({ ...uploadForm, requires_signature: e.target.checked })}
                    className="rounded"
                  />
                  Requires Signature
                </label>
              </div>
              <div>
                <DropZone
                  onFile={(f) => handleFileUpload(f)}
                  accept=".pdf,.doc,.docx,application/pdf"
                  acceptExtensions={['pdf', 'doc', 'docx']}
                  label="Drag and drop a document"
                  hint="PDF, DOC, or DOCX"
                  disabled={!uploadForm.name}
                  testId="sigdoc-upload"
                  compact
                />
                {!uploadForm.name && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter a document name above to enable upload.
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Create Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Create Template</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowTemplateModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Template Name *</label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Standard Offer Letter"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Placeholders</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Add placeholders like "applicant_name" that will be replaced with actual values
                </p>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newPlaceholder}
                    onChange={(e) => setNewPlaceholder(e.target.value.replace(/\s/g, '_'))}
                    placeholder="e.g., salary, position, start_date"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPlaceholder())}
                  />
                  <Button type="button" onClick={addPlaceholder}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {templateForm.placeholders.map((p) => (
                    <Badge key={p} className="bg-muted flex items-center gap-1">
                      {`{{${p}}}`}
                      <button onClick={() => removePlaceholder(p)} className="ml-1 hover:text-destructive">
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Template Content *</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Use placeholders like {`{{applicant_name}}`} in your content
                </p>
                <textarea
                  className="w-full min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={templateForm.template_content}
                  onChange={(e) => setTemplateForm({ ...templateForm, template_content: e.target.value })}
                  placeholder="Dear {{applicant_name}},&#10;&#10;We are pleased to offer you the position of {{position}} at Elite Home Rehab...&#10;&#10;Salary: ${{salary}}&#10;Start Date: {{start_date}}&#10;&#10;..."
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={templateForm.is_offer_letter}
                    onChange={(e) => setTemplateForm({ ...templateForm, is_offer_letter: e.target.checked })}
                    className="rounded"
                  />
                  Offer Letter
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={templateForm.requires_signature}
                    onChange={(e) => setTemplateForm({ ...templateForm, requires_signature: e.target.checked })}
                    className="rounded"
                  />
                  Requires Signature
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTemplate}>
                  Create Template
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Request Signature Modal */}
      {showRequestModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Request Signature</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowRequestModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="space-y-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm font-medium">Document: {selectedDocument.name}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Recipient Type</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                  value={requestForm.recipient_type}
                  onChange={(e) => handleRecipientChange(e.target.value, '')}
                >
                  <option value="applicant">Applicant</option>
                  <option value="personnel">Personnel</option>
                  <option value="external">External (Manual Entry)</option>
                </select>
              </div>

              {requestForm.recipient_type === 'applicant' && (
                <div>
                  <label className="text-sm font-medium">Select Applicant</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                    value={requestForm.recipient_id}
                    onChange={(e) => handleRecipientChange('applicant', e.target.value)}
                  >
                    <option value="">Select an applicant...</option>
                    {applicants.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.last_name}, {a.first_name} - {a.email || 'No email'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {requestForm.recipient_type === 'personnel' && (
                <div>
                  <label className="text-sm font-medium">Select Personnel</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                    value={requestForm.recipient_id}
                    onChange={(e) => handleRecipientChange('personnel', e.target.value)}
                  >
                    <option value="">Select a person...</option>
                    {personnel.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.last_name}, {p.first_name} - {p.email || 'No email'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Recipient Name *</label>
                <Input
                  value={requestForm.recipient_name}
                  onChange={(e) => setRequestForm({ ...requestForm, recipient_name: e.target.value })}
                  placeholder="Full name"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Recipient Email *</label>
                <Input
                  type="email"
                  value={requestForm.recipient_email}
                  onChange={(e) => setRequestForm({ ...requestForm, recipient_email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>

              {/* Placeholder values for templates */}
              {selectedDocument.placeholders && selectedDocument.placeholders.length > 0 && (
                <div className="space-y-3">
                  <label className="text-sm font-medium">Fill in Template Values</label>
                  {selectedDocument.placeholders.map((placeholder) => (
                    <div key={placeholder}>
                      <label className="text-xs text-muted-foreground capitalize">
                        {placeholder.replace(/_/g, ' ')}
                      </label>
                      <Input
                        value={requestForm.placeholder_values[placeholder] || ''}
                        onChange={(e) => setRequestForm({
                          ...requestForm,
                          placeholder_values: {
                            ...requestForm.placeholder_values,
                            [placeholder]: e.target.value,
                          },
                        })}
                        placeholder={`Enter ${placeholder.replace(/_/g, ' ')}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={requestForm.send_email}
                  onChange={(e) => setRequestForm({ ...requestForm, send_email: e.target.checked })}
                  className="rounded"
                />
                Send email notification
              </label>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowRequestModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSendRequest}>
                  <Send size={16} className="mr-2" />
                  Send Request
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
